import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Select from 'react-select';
import 'react-toastify/dist/ReactToastify.css';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'hod', label: 'Head of Department' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'employee', label: 'Employee' },
  { value: 'coo', label: 'COO' },
  { value: 'cos', label: 'COS' },
  { value: 'supervisor', label: 'Supervisor' }
];

const SystemUpdates = () => {
  const [recipientType, setRecipientType] = useState('role');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  
  const baseUrl = API_BASE_URL;

  useEffect(() => {
    if (recipientType === 'individual' && employees.length === 0) {
      fetchEmployees();
    }
  }, [recipientType]);

  const fetchEmployees = async () => {
    setFetchingEmployees(true);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`${baseUrl}/api/employees/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setEmployees(data);
    } catch (err) {
      console.error('Failed to fetch employees', err);
      toast.error('Failed to fetch employees.');
    } finally {
      setFetchingEmployees(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    if (recipientType === 'role' && selectedRoles.length === 0) {
      toast.error("Please select at least one role.");
      return;
    }
    if (recipientType === 'individual' && selectedEmployees.length === 0) {
      toast.error("Please select at least one employee.");
      return;
    }

    setLoading(true);
    try {
      const token = Cookies.get('token');
      const payload = {
        recipient_type: recipientType,
        subject: subject.trim(),
        message: message.trim(),
        roles: recipientType === 'role' ? selectedRoles.map(r => r.value) : [],
        employee_ids: recipientType === 'individual' ? selectedEmployees.map(e => e.value) : []
      };

      const response = await axios.post(`${baseUrl}/api/sys-updates/`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(response.data.detail || 'System update sent successfully!');
      
      // Reset form
      setSubject('');
      setMessage('');
      setSelectedRoles([]);
      setSelectedEmployees([]);
      
    } catch (err) {
      console.error('Error sending system update:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || 'Failed to send system update.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const employeeOptions = employees.map(emp => ({
    value: emp.id,
    label: `${emp.first_name} ${emp.last_name} (${emp.code || emp.email})`
  }));

  return (
    <>
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} />
      <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
        <Topbar />
        <Sidebar />

        <div className="page-wrapper">
          <div className="content pb-0">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
              <div>
                <h4 className="fw-bold mb-1">System Updates</h4>
                <p className="text-muted mb-0 fs-13">Send system-wide notifications and updates via email</p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card shadow-sm mb-4">
                <div className="card-header">
                  <h5 className="fw-bold mb-0">Update Details</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-medium">Recipient Type <span className="text-danger">*</span></label>
                      <div className="d-flex gap-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="recipientType"
                            id="typeRole"
                            value="role"
                            checked={recipientType === 'role'}
                            onChange={(e) => setRecipientType(e.target.value)}
                            disabled={loading}
                          />
                          <label className="form-check-label" htmlFor="typeRole">
                            By Role
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="recipientType"
                            id="typeIndividual"
                            value="individual"
                            checked={recipientType === 'individual'}
                            onChange={(e) => setRecipientType(e.target.value)}
                            disabled={loading}
                          />
                          <label className="form-check-label" htmlFor="typeIndividual">
                            Specific Employees
                          </label>
                        </div>
                      </div>
                    </div>

                    {recipientType === 'role' && (
                      <div className="col-12">
                        <label className="form-label">Select Roles <span className="text-danger">*</span></label>
                        <Select
                          isMulti
                          classNamePrefix="react-select"
                          options={ROLE_OPTIONS}
                          value={selectedRoles}
                          onChange={setSelectedRoles}
                          placeholder="Select roles to notify..."
                          isDisabled={loading}
                        />
                      </div>
                    )}

                    {recipientType === 'individual' && (
                      <div className="col-12">
                        <label className="form-label">Select Employees <span className="text-danger">*</span></label>
                        <Select
                          isMulti
                          classNamePrefix="react-select"
                          isLoading={fetchingEmployees}
                          options={employeeOptions}
                          value={selectedEmployees}
                          onChange={setSelectedEmployees}
                          placeholder={fetchingEmployees ? 'Loading employees...' : 'Select employees to notify...'}
                          isDisabled={loading || fetchingEmployees}
                        />
                      </div>
                    )}

                    <div className="col-12">
                      <label htmlFor="subject" className="form-label">Subject <span className="text-danger">*</span></label>
                      <input
                        id="subject"
                        type="text"
                        className="form-control"
                        placeholder="Enter update subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="col-12">
                      <label htmlFor="message" className="form-label">Message Content <span className="text-danger">*</span></label>
                      <textarea
                        id="message"
                        className="form-control"
                        rows="6"
                        placeholder="Enter the system update details..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        disabled={loading}
                        style={{ resize: 'vertical' }}
                      />
                      <small className="text-muted mt-1 d-block">
                        This message will be included in the email sent to the selected users.
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setSubject('');
                    setMessage('');
                    setSelectedRoles([]);
                    setSelectedEmployees([]);
                  }}
                  disabled={loading}
                >
                  Clear Form
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    loading || 
                    !subject.trim() || 
                    !message.trim() || 
                    (recipientType === 'role' && selectedRoles.length === 0) ||
                    (recipientType === 'individual' && selectedEmployees.length === 0)
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Sending Update...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-send me-1" />
                      Send Update
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
            <p className="text-dark mb-0">
              2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SystemUpdates;